// ./src/components/features/VolunteerRoles/VolunteerRolesList/VolunteerRolesList.tsx
//
// Volunteer Roles list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Mirrors the Policies list conventions (see em-scaffold-ui).

import React, { useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { VolunteerRole } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalVolunteerRolesContext } from "@/contexts/VolunteerRolesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface VolunteerRolesListProps {
  data?: VolunteerRole[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditRole?: (role: VolunteerRole) => void;
  onDeleteRole?: (role: VolunteerRole) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddRole?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search/filter is active — shows a "no match" empty state instead of the "add first" one. */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (role: VolunteerRole): boolean => role.status === STATUSES.DISABLED;

const hasText = (value?: string | null): boolean => !!value && value.trim().length > 0;

const StatusBadge: React.FC<{ role: VolunteerRole }> = ({ role }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(role)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// What this role documents — responsibilities / eligibility presence.
const DetailBadges: React.FC<{ role: VolunteerRole }> = ({ role }) => {
  const { t } = useTranslation(["volunteerRoles"]);

  const paid = role.hourlyRate != null;

  if (!hasText(role.responsibilities) && !hasText(role.eligibility) && !paid) {
    return (
      <Text size="xs" c="dimmed">
        {t("volunteerRoles:detail.none", { defaultValue: "—" })}
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="wrap">
      {paid && (
        <Badge size="xs" variant="light" color="teal" leftSection={<AppIcon iconName="Coin" size={10} />}>
          {t("volunteerRoles:detail.paid", {
            defaultValue: "Paid · {{rate}} / h",
            rate: Number(role.hourlyRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          })}
        </Badge>
      )}
      {hasText(role.responsibilities) && (
        <Badge size="xs" variant="light" color="grape" leftSection={<AppIcon iconName="ListCheck" size={10} />}>
          {t("volunteerRoles:detail.responsibilities", { defaultValue: "Responsibilities" })}
        </Badge>
      )}
      {hasText(role.eligibility) && (
        <Badge size="xs" variant="outline" color="teal" leftSection={<AppIcon iconName="UserCheck" size={10} />}>
          {t("volunteerRoles:detail.eligibility", { defaultValue: "Eligibility" })}
        </Badge>
      )}
    </Group>
  );
};

const RoleIcon: React.FC<{ role: VolunteerRole; size?: number }> = ({ role, size = 34 }) => (
  <ThemeIcon variant="light" color={role.color || "grape"} radius="xl" size={size}>
    <AppIcon iconName={role.icon || "Users"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  role: VolunteerRole;
  onEdit?: (r: VolunteerRole) => void;
  onDelete?: (r: VolunteerRole) => void;
  isDeleting: boolean;
}> = ({ role, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(role, "edit");
  const showDelete = !!onDelete && recordHasRight(role, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(role)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(role)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  role: VolunteerRole;
  name: string;
  disabled: boolean;
  onEdit?: (r: VolunteerRole) => void;
  onDelete?: (r: VolunteerRole) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const RoleTableRow: React.FC<RowProps> = ({
  role,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => role.id && onToggleSelect?.(role.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <RoleIcon role={role} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge role={role} />
            </Group>
            {role.summary && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {role.summary}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 320 }}>
        <DetailBadges role={role} />
      </Table.Td>
      <Table.Td style={{ width: 170 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(role.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions role={role} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const RoleTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 320 }}>
      <Skeleton height={18} width={140} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const RoleCardComponent: React.FC<CardProps> = ({
  role,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => role.id && onToggleSelect?.(role.id);

  const swipeActions = React.useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(role, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(role),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(role, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(role),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, role, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <RoleIcon role={role} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge role={role} />
          </Group>
          {role.summary && (
            <Text size="xs" c="dimmed" lineClamp={1} mt={2}>
              {role.summary}
            </Text>
          )}
          <Box mt={6}>
            <DetailBadges role={role} />
          </Box>
        </Box>
        {showActions && (
          <RowActions role={role} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const RoleCard = React.memo(RoleCardComponent);

const RoleCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "xs", "xs"]} gap={4} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const VolunteerRolesListComponent: React.FC<VolunteerRolesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditRole,
  onDeleteRole,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddRole,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["volunteerRoles", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditRole, onDeleteRole);

  const rolesContext = useOptionalVolunteerRolesContext();

  const roles = dataProp ?? rolesContext?.volunteerRoles ?? [];
  const loading = loadingProp ?? rolesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? rolesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? rolesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? rolesContext?.loadMore;
  const isRemoving = isRemovingProp ?? rolesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: roles.length });

  const handleEdit = useCallback(
    (role: VolunteerRole) => {
      closeAllCards();
      onEditRole?.(role);
    },
    [onEditRole, closeAllCards]
  );

  const handleDelete = useCallback(
    (role: VolunteerRole) => {
      closeAllCards();
      onDeleteRole?.(role);
    },
    [onDeleteRole, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = rolesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && roles.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("volunteerRoles:emptyState.noMatchTitle", { defaultValue: "No roles match" })}
            description={t("volunteerRoles:emptyState.noMatchDescription", {
              defaultValue: "Try a different search or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("volunteerRoles:emptyState.title", { defaultValue: "No volunteer roles yet" })}
          description={t("volunteerRoles:emptyState.description", {
            defaultValue: "Define reusable roles (check-in staff, route marshal…) to staff your events.",
          })}
          iconName="Users"
          actionLabel={t("volunteerRoles:emptyState.actionLabel", { defaultValue: "Add role" })}
          onAction={onAddRole}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (role: VolunteerRole): boolean =>
    isSelectable &&
    ((!!onEditRole && recordHasRight(role, "edit")) ||
      (!!onDeleteRole && recordHasRight(role, "remove")));

  const rowProps = (role: VolunteerRole): RowProps => ({
    role,
    name: role.name || "—",
    disabled: isDisabled(role),
    onEdit: onEditRole ? handleEdit : undefined,
    onDelete: onDeleteRole ? handleDelete : undefined,
    highlighted: role.id === highlightedId,
    isDeleting: isRemoving(role.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(role),
    selected: selection?.isSelected(role.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = roles
    .filter((r) => rowIsSelectable(r))
    .map((r) => r.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("volunteerRoles:list.columns.name", { defaultValue: "Role" })}</Table.Th>
                <Table.Th style={{ width: 320 }}>
                  {t("volunteerRoles:list.columns.details", { defaultValue: "Details" })}
                </Table.Th>
                <Table.Th style={{ width: 170 }}>
                  {t("volunteerRoles:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <RoleTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : roles.map((role) => <RoleTableRow key={role.id} {...rowProps(role)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <RoleCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {roles.map((role) => (
            <motion.div
              key={role.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <RoleCard
                {...rowProps(role)}
                forceClose={shouldForceClose(role.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(role.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(role.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const VolunteerRolesList = React.memo(VolunteerRolesListComponent);
